import { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import {
  Search, Heart, ShoppingCart, MapPin, User, Menu, X,
  LayoutGrid, Store, Shield, Crown, LogOut, Package, Home, ChevronDown, Store as SellerIcon, Truck, ShieldCheck,
} from "lucide-react";
import api from "@/lib/api";
import { BRAND } from "@/constants/branding";
import { useAuth } from "@/context/AuthContext";
import { useStore } from "@/context/StoreContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const initials = (name) =>
  (name || "")
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0])
    .join("")
    .toUpperCase() || "U";

export default function Header() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const { cart, wishlist, settings } = useStore();
  const [q, setQ] = useState("");
  const [mobileOpen, setMobileOpen] = useState(false);
  const [defaultAddr, setDefaultAddr] = useState(null);
  const [cats, setCats] = useState([]);

  const pv = settings?.panel_visibility || { vendor: true, admin: true, super_admin: true };

  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  useEffect(() => {
    api.get("/categories").then((r) => setCats(r.data || [])).catch(() => {});
  }, []);

  useEffect(() => {
    let active = true;
    async function loadAddr() {
      if (!user || user.role !== "customer") { setDefaultAddr(null); return; }
      try {
        const { data } = await api.get("/addresses");
        if (active) setDefaultAddr(data.find((a) => a.is_default) || data[0] || null);
      } catch { if (active) setDefaultAddr(null); }
    }
    loadAddr();
    return () => { active = false; };
  }, [user, location.pathname]);

  const submitSearch = (e) => {
    e.preventDefault();
    navigate(`/products?search=${encodeURIComponent(q.trim())}`);
  };

  const panels = [];
  if (user?.role === "vendor" && pv.vendor) panels.push({ label: "Vendor", to: "/vendor", icon: Store });
  if ((user?.role === "admin" || user?.role === "super_admin") && pv.admin) panels.push({ label: "Admin", to: "/admin", icon: Shield });
  if (user?.role === "super_admin" && pv.super_admin) panels.push({ label: "Super Admin", to: "/super-admin", icon: Crown });

  const locationLabel = defaultAddr ? `${defaultAddr.city} ${defaultAddr.pincode}` : "Add delivery address";

  const navLink = "relative whitespace-nowrap py-3 text-[13px] font-semibold text-foreground/80 hover:text-primary transition-colors after:absolute after:left-0 after:right-0 after:bottom-0 after:h-[2px] after:bg-primary after:scale-x-0 hover:after:scale-x-100 after:transition-transform after:origin-left";

  return (
    <>
      {/* Utility bar */}
      <div className="hidden sm:block bg-deep text-white/80 text-xs" data-testid="header-topbar">
        <div className="shell flex items-center justify-between h-9">
          <div className="flex items-center gap-5">
            <span className="flex items-center gap-1.5"><Truck className="h-3.5 w-3.5 text-gold" /> Free delivery on every order</span>
            <span className="hidden md:flex items-center gap-1.5"><ShieldCheck className="h-3.5 w-3.5 text-gold" /> Secure payments</span>
          </div>
          <div className="flex items-center gap-5">
            <Link to="/register?role=vendor" className="flex items-center gap-1.5 hover:text-white transition-colors" data-testid="header-become-seller">
              <SellerIcon className="h-3.5 w-3.5 text-gold" /> Become a Seller
            </Link>
            <Link to="/help" className="hover:text-white transition-colors">Help</Link>
            <Link to="/contact" className="hover:text-white transition-colors">Contact</Link>
          </div>
        </div>
      </div>

      <header className="sticky top-0 z-50 bg-white border-b border-border shadow-[0_6px_20px_-14px_rgba(6,48,28,0.35)]" data-testid="site-header">
        <div className="shell">
          <div className="flex items-center gap-3 sm:gap-6 h-16 sm:h-[72px]">
            <button className="lg:hidden p-1.5 -ml-1.5 text-foreground" onClick={() => setMobileOpen((v) => !v)} data-testid="header-mobile-toggle" aria-label="Menu">
              {mobileOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>

            <Link to="/" className="flex items-center gap-2.5 shrink-0" data-testid="header-logo">
              <img src="/zilkart-bag.png" alt="Zilkart" className="h-9 sm:h-11 w-auto object-contain" />
              <span className="flex flex-col leading-none">
                <span className="text-[22px] sm:text-[27px] font-extrabold tracking-tight">
                  <span className="text-deep">ZIL</span><span className="text-primary">KART</span>
                </span>
                <span className="hidden lg:block mt-1 text-[9.5px] font-semibold tracking-[0.18em] uppercase text-muted-foreground">Online Shopping Made Easy</span>
              </span>
            </Link>

            {/* Search */}
            <form onSubmit={submitSearch} className="flex-1 min-w-[200px] hidden sm:flex max-w-2xl mx-auto">
              <div className="relative w-full">
                <Input
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="Search products & brands"
                  className="h-11 rounded-full bg-secondary border border-transparent pl-5 pr-14 text-sm placeholder:text-muted-foreground focus-visible:bg-white focus-visible:border-primary focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:ring-offset-0"
                  data-testid="header-search-input"
                />
                <button type="submit" className="absolute right-1 top-1 h-9 w-9 rounded-full bg-primary hover:bg-primary/90 text-white flex items-center justify-center transition-colors" aria-label="Search">
                  <Search className="h-4 w-4" />
                </button>
              </div>
            </form>

            <div className="flex items-center gap-1 sm:gap-2 ml-auto text-foreground">
              {user?.role === "customer" && (
                <button onClick={() => navigate("/account?tab=addresses")} className="hidden xl:flex flex-col items-start leading-tight px-3 py-1.5 rounded-lg hover:bg-secondary transition-colors max-w-[170px]" data-testid="header-location-button">
                  <span className="text-[11px] text-muted-foreground flex items-center gap-1"><MapPin className="h-3 w-3" /> Deliver to</span>
                  <span className="text-sm font-semibold truncate w-full text-left">{locationLabel}</span>
                </button>
              )}

              {user ? (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button className="hidden sm:flex items-center gap-2 font-semibold text-sm pl-1.5 pr-2.5 py-1.5 rounded-full hover:bg-secondary transition-colors" data-testid="header-profile-button">
                      <span className="h-8 w-8 rounded-full bg-primary text-white text-xs font-bold flex items-center justify-center">{initials(user.name)}</span>
                      <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-56">
                    <DropdownMenuLabel className="truncate">
                      <div className="font-semibold">{user.name}</div>
                      <div className="text-xs text-muted-foreground font-normal capitalize">{user.role.replace("_", " ")}</div>
                    </DropdownMenuLabel>
                    <DropdownMenuSeparator />
                    {user.role === "customer" && (
                      <>
                        <DropdownMenuItem onClick={() => navigate("/account?tab=profile")} data-testid="menu-profile"><User className="h-4 w-4 mr-2" /> My Profile</DropdownMenuItem>
                        <DropdownMenuItem onClick={() => navigate("/account?tab=orders")} data-testid="menu-orders"><Package className="h-4 w-4 mr-2" /> My Orders</DropdownMenuItem>
                        <DropdownMenuItem onClick={() => navigate("/account?tab=addresses")} data-testid="menu-addresses"><MapPin className="h-4 w-4 mr-2" /> Addresses</DropdownMenuItem>
                        <DropdownMenuItem onClick={() => navigate("/wishlist")} data-testid="menu-wishlist"><Heart className="h-4 w-4 mr-2" /> Wishlist</DropdownMenuItem>
                      </>
                    )}
                    {panels.map((p) => (
                      <DropdownMenuItem key={p.to} onClick={() => navigate(p.to)} data-testid={`menu-panel-${p.label.toLowerCase().replace(/\s+/g, "-")}`}>
                        <p.icon className="h-4 w-4 mr-2" /> {p.label} Panel
                      </DropdownMenuItem>
                    ))}
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={() => { logout(); navigate("/"); }} data-testid="menu-logout"><LogOut className="h-4 w-4 mr-2" /> Logout</DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              ) : (
                <Button size="sm" onClick={() => navigate("/login")} className="hidden sm:inline-flex rounded-full bg-primary text-white hover:bg-primary/90 font-semibold px-6 h-10" data-testid="header-login-button">
                  Login
                </Button>
              )}

              {panels.length > 0 && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button className="hidden md:flex items-center gap-1 font-semibold text-sm px-3 py-2 rounded-full hover:bg-secondary transition-colors" data-testid="panel-role-switcher">
                      <LayoutGrid className="h-4 w-4" /> Panels <ChevronDown className="h-3 w-3" />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-48">
                    <DropdownMenuLabel>Switch panel</DropdownMenuLabel>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={() => navigate("/")} data-testid="panel-switch-storefront"><Home className="h-4 w-4 mr-2" /> Storefront</DropdownMenuItem>
                    {panels.map((p) => (
                      <DropdownMenuItem key={p.to} onClick={() => navigate(p.to)} data-testid={`panel-switch-${p.label.toLowerCase().replace(/\s+/g, "-")}`}>
                        <p.icon className="h-4 w-4 mr-2" /> {p.label}
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
              )}

              <button onClick={() => navigate(user ? "/wishlist" : "/login")} className="relative h-10 w-10 flex items-center justify-center rounded-full hover:bg-secondary transition-colors" data-testid="header-wishlist-button" aria-label="Wishlist">
                <Heart className="h-5 w-5" />
                {wishlist.length > 0 && <span className="absolute top-0 right-0 bg-primary text-white text-[10px] font-bold rounded-full h-4 min-w-4 px-1 flex items-center justify-center">{wishlist.length}</span>}
              </button>

              <button onClick={() => navigate("/cart")} className="relative h-10 flex items-center gap-3 px-3 rounded-full hover:bg-secondary transition-colors font-semibold text-sm" data-testid="header-cart-button" aria-label="Cart">
                <div className="relative">
                  <ShoppingCart className="h-5 w-5" />
                  {cart.count > 0 && <span className="absolute -top-2 -right-2.5 bg-primary text-white text-[10px] font-bold rounded-full h-4 min-w-4 px-1 flex items-center justify-center" data-testid="header-cart-count">{cart.count}</span>}
                </div>
                <span className="hidden lg:inline">Cart</span>
              </button>
            </div>
          </div>

          {/* Mobile search */}
          <form onSubmit={submitSearch} className="sm:hidden pb-3">
            <div className="relative">
              <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search products…" className="h-10 rounded-full bg-secondary border-transparent pl-4 pr-12 focus-visible:bg-white focus-visible:border-primary focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:ring-offset-0" data-testid="header-search-input-mobile" />
              <button type="submit" className="absolute right-1 top-1 h-8 w-8 rounded-full bg-primary text-white flex items-center justify-center" aria-label="Search"><Search className="h-4 w-4" /></button>
            </div>
          </form>
        </div>

        {/* Category strip */}
        {cats.length > 0 && (
          <div className="hidden md:block border-t border-border" data-testid="header-category-strip">
            <div className="shell">
              <nav className="flex items-center justify-center gap-7 lg:gap-10 overflow-x-auto no-scrollbar">
                <Link to="/products" className={navLink} data-testid="strip-all">All Products</Link>
                {cats.map((c) => (
                  <Link key={c.slug} to={`/products?category=${c.slug}`} className={navLink} data-testid={`strip-${c.slug}`}>{c.name}</Link>
                ))}
                <Link to="/products?featured=1" className="relative whitespace-nowrap py-3 text-[13px] font-bold text-[#9a741a] hover:text-deep transition-colors" data-testid="strip-deals">Today&apos;s Deals</Link>
              </nav>
            </div>
          </div>
        )}

      {/* Mobile menu */}
      {mobileOpen && (
        <div className="lg:hidden bg-white px-5 py-3 space-y-1 border-t border-border max-h-[70vh] overflow-y-auto" data-testid="mobile-menu">
          {[
            { to: "/", label: "Home" },
            { to: "/products", label: "All Products" },
            { to: "/categories", label: "Categories" },
            { to: "/register?role=vendor", label: "Become a Seller" },
            { to: "/about", label: "About" },
            { to: "/contact", label: "Contact" },
          ].map((l) => (
            <Link key={l.label} to={l.to} className="block py-2.5 text-sm font-semibold" data-testid={`mobile-nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}>{l.label}</Link>
          ))}
          {cats.length > 0 && <p className="pt-2 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Shop by category</p>}
          {cats.map((c) => (
            <Link key={c.slug} to={`/products?category=${c.slug}`} className="block py-2 text-sm" data-testid={`mobile-cat-${c.slug}`}>{c.name}</Link>
          ))}
          {!user && <Link to="/login" className="block py-2 text-sm font-semibold text-primary" data-testid="mobile-nav-login">Login</Link>}
          {panels.map((p) => (
            <Link key={p.to} to={p.to} className="flex items-center gap-2 py-2 text-sm font-semibold text-primary"><p.icon className="h-4 w-4" /> {p.label} Panel</Link>
          ))}
        </div>
      )}
      </header>
    </>
  );
}
